import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Plus, Moon, Stars } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface DreamEntry {
  id: string;
  dream_date: string;
  created_at: string;
  title: string;
  description: string;
  symbols: string[];
  themes: string[];
  emotional_tone: string;
  lucidity_level: number;
  personal_interpretation: string;
  ai_analysis: string;
}

export default function DreamJournal() {
  const [dreams, setDreams] = useState<DreamEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    symbols: '',
    themes: '',
    emotional_tone: '',
    lucidity_level: 0,
    personal_interpretation: ''
  });

  useEffect(() => {
    loadDreams();
  }, []);

  const loadDreams = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('dream_entries')
      .select('*')
      .eq('user_id', user.id)
      .order('dream_date', { ascending: false });

    if (error) {
      toast({ title: 'Error loading dreams', variant: 'destructive' });
    } else {
      setDreams(data || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('dream_entries')
      .insert({
        user_id: user.id,
        dream_date: new Date().toISOString().split('T')[0],
        title: formData.title,
        description: formData.description,
        symbols: formData.symbols.split(',').map(s => s.trim()).filter(s => s),
        themes: formData.themes.split(',').map(t => t.trim()).filter(t => t),
        emotional_tone: formData.emotional_tone,
        lucidity_level: formData.lucidity_level,
        personal_interpretation: formData.personal_interpretation
      });

    if (error) {
      toast({ title: 'Error saving dream', variant: 'destructive' });
    } else {
      toast({ title: 'Dream logged' });
      setFormData({
        title: '',
        description: '',
        symbols: '',
        themes: '',
        emotional_tone: '',
        lucidity_level: 0,
        personal_interpretation: ''
      });
      setShowForm(false);
      loadDreams();
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading dream journal...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Moon className="h-5 w-5 text-primary animate-pulse" />
            Dream Symbolism Library
          </CardTitle>
          <CardDescription>
            Record dreams and identify archetypal symbols, spiritual messages, and psychic impressions received during sleep. Dreams are a powerful gateway to the subconscious and spirit realm.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setShowForm(!showForm)} className="mb-4">
            <Plus className="h-4 w-4 mr-2" />
            Log Dream
          </Button>

          {showForm && (
            <form onSubmit={handleSubmit} className="space-y-4 mb-6 p-4 border border-border rounded-lg bg-card animate-fade-in">
              <div className="space-y-2">
                <Label>Dream Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Give your dream a memorable title"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Dream Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your dream in as much detail as you remember..."
                  rows={6}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Key Symbols (comma separated)</Label>
                <Input
                  value={formData.symbols}
                  onChange={(e) => setFormData({ ...formData, symbols: e.target.value })}
                  placeholder="water, snake, flying, house, etc."
                />
              </div>

              <div className="space-y-2">
                <Label>Themes (comma separated)</Label>
                <Input
                  value={formData.themes}
                  onChange={(e) => setFormData({ ...formData, themes: e.target.value })}
                  placeholder="transformation, fear, love, journey, etc."
                />
              </div>

              <div className="space-y-2">
                <Label>Emotional Tone</Label>
                <Input
                  value={formData.emotional_tone}
                  onChange={(e) => setFormData({ ...formData, emotional_tone: e.target.value })}
                  placeholder="Peaceful, anxious, excited, mysterious, etc."
                />
              </div>

              <div className="space-y-2">
                <Label>Lucidity Level: {formData.lucidity_level}/10</Label>
                <Slider
                  value={[formData.lucidity_level]}
                  onValueChange={([value]) => setFormData({ ...formData, lucidity_level: value })}
                  min={0}
                  max={10}
                  step={1}
                />
              </div>

              <div className="space-y-2">
                <Label>Your Interpretation</Label>
                <Textarea
                  value={formData.personal_interpretation}
                  onChange={(e) => setFormData({ ...formData, personal_interpretation: e.target.value })}
                  placeholder="What do you think this dream means? Any insights or messages?"
                  rows={3}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit">Save Dream</Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          )}

          <div className="space-y-4">
            {dreams.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Start logging dreams to track patterns, symbols, and spiritual messages.
              </p>
            ) : (
              dreams.map((dream) => (
                <Card key={dream.id} className="bg-muted/30 animate-fade-in">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Stars className="h-4 w-4 text-primary animate-pulse" />
                          {dream.title}
                        </CardTitle>
                        <div className="flex gap-2 flex-wrap">
                          {dream.symbols?.map((symbol, idx) => (
                            <Badge key={idx} variant="secondary">{symbol}</Badge>
                          ))}
                        </div>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(dream.dream_date), 'PPP')}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm">{dream.description}</p>
                    {dream.emotional_tone && (
                      <div>
                        <p className="text-sm font-light text-primary mb-1">Emotional Tone:</p>
                        <p className="text-sm text-muted-foreground font-light">{dream.emotional_tone}</p>
                      </div>
                    )}
                    {dream.personal_interpretation && (
                      <div className="pt-2 border-t border-border/50">
                        <p className="text-sm font-light text-primary mb-1">Interpretation:</p>
                        <p className="text-sm text-muted-foreground font-light italic">{dream.personal_interpretation}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
